/**
 * BackToGamesBar — context-aware global back navigation (DESKTOP).
 *
 * Walks UP the URL by one segment per click and labels the destination
 * accordingly. At `/sudoku` the bar says "← All games" and goes home; at
 * `/quiet-hours/morning-calm` the bar says "← Quiet Hours" and goes to the
 * Quiet Hours hub; at `/quiet-hours/morning-calm/5` the bar says "← Morning
 * Calm" and goes to the SessionConfig.
 *
 * Variants must NOT implement their own "back to <parent>" buttons — this
 * bar is the single global affordance. (Variants can still have explicit
 * "End session" / "Quit" buttons for actions with confirmation semantics —
 * those are distinct from URL navigation.)
 *
 * Visual: mirrors <PreferencesBar /> at top-left. Same pill, same Y, same
 * paper material. Together they form one horizontal chrome row.
 *
 * Below 768px the labeled pill is replaced by a single circular icon
 * (<MobileBackButton />); this component returns null there. The
 * destination logic in `backTargetFor()` is shared between the two
 * presentations — only the visual changes.
 *
 * **It does not render at all when embedded.** See `host/isEmbedded.ts`: a host
 * that put one title on screen owns navigation, and this pill would be a door
 * out of whatever that host was enforcing. In the resident Library it is a
 * one-tap bypass of the dementia-fit gate — from a memory-care screen showing
 * five approved titles, "← All games" reaches all thirteen. Standalone is
 * unchanged.
 */
import { Link, useLocation } from 'react-router';

import { isEmbedded } from '@/host/isEmbedded';
import { useIsMobileChrome } from '@/preferences/useIsMobileChrome';

import './BackToGamesBar.css';

/**
 * Slugs whose display name doesn't fall out of plain titlecasing.
 */
const DISPLAY_NAMES: Record<string, string> = {
  'quiet-hours': 'Quiet Hours',
  'word-search': 'Word Search',
};

/**
 * Explicit per-route overrides. Use when URL-walk would land on a routing
 * detail rather than a meaningful destination. The keys are full pathnames;
 * values are the explicit back target. Query strings are not matched —
 * compare the location's pathname only.
 */
const ROUTE_OVERRIDES: Record<string, BackTarget> = {
  // The /play/* routes are GameHostPage mounts — "/play" itself isn't a
  // page users navigate to. Send back to each game's own hub.
  '/play/five': { to: '/five', label: 'Five' },
  '/play/crossword': { to: '/crossword', label: 'Crossword' },
  '/play/sudoku': { to: '/sudoku', label: 'Sudoku' },
  '/play/word-search': { to: '/word-search', label: 'Word Search' },
  '/play/trivia': { to: '/trivia', label: 'Trivia' },
  // Balloons is deep-link-only (no per-game hub) — back goes to the games hub.
  '/play/balloons': { to: '/', label: 'Games' },
  '/play/categories': { to: '/categories', label: 'Categories' },
  '/play/wheel': { to: '/wheel', label: 'The Wheel' },
  '/wheel-lab': { to: '/wheel', label: 'The Wheel' },
};

function titleCase(slug: string): string {
  return slug
    .split('-')
    .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function displayNameFor(slug: string): string {
  return DISPLAY_NAMES[slug] ?? titleCase(slug);
}

interface BackTarget {
  to: string;
  label: string;
}

/** Compute where "back" goes from the current path, or null at root. */
export function backTargetFor(pathname: string): BackTarget | null {
  if (pathname === '/' || pathname === '') return null;

  // Explicit overrides win — used for routes whose URL parent isn't a
  // meaningful destination (the /play/* prefix is a mount point, not a page).
  if (ROUTE_OVERRIDES[pathname]) {
    return ROUTE_OVERRIDES[pathname];
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  if (segments.length === 1) {
    // /sudoku, /five, /quiet-hours, /trivia, /word-search
    return { to: '/', label: 'All games' };
  }

  // 2+ segments: walk up one. The parent is the segment immediately above.
  const parentSegments = segments.slice(0, -1);
  const parentSlug = parentSegments[parentSegments.length - 1] ?? '';
  const parentPath = '/' + parentSegments.join('/');
  return { to: parentPath, label: displayNameFor(parentSlug) };
}

export function BackToGamesBar() {
  const location = useLocation();
  const target = backTargetFor(location.pathname);
  const isMobile = useIsMobileChrome();

  // Embedded: the host is the navigator and supplies its own exit.
  if (isEmbedded()) return null;
  if (!target) return null;
  // Mobile presentation lives in <MobileBackButton />.
  if (isMobile) return null;

  return (
    <div className="back-bar" role="group" aria-label="Navigation">
      <Link
        to={target.to}
        className="back-bar__btn"
        aria-label={`Back to ${target.label}`}
      >
        <span className="back-bar__arrow" aria-hidden="true">
          ←
        </span>
        <span className="back-bar__label">{target.label}</span>
      </Link>
    </div>
  );
}
